import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { FaTimes, FaPlus, FaTrash, FaArrowUp, FaArrowDown, FaEdit, FaSave, FaUndo } from 'react-icons/fa';

interface FormField {
  id: string;
  type: string;
  label: string;
  required: boolean;
  options?: string[];
  placeholder?: string;
}

interface FormStep {
  id: string;
  title: string;
  description: string;
  fields: FormField[];
}

interface QuotationFormBuilderProps {
  isOpen: boolean;
  onClose: () => void;
}

const QuotationFormBuilder: React.FC<QuotationFormBuilderProps> = ({
  isOpen,
  onClose
}) => {
  const { t, i18n } = useTranslation();
  const { userData } = useAuth();
  const [steps, setSteps] = useState<FormStep[]>([]);
  const [newField, setNewField] = useState<Partial<FormField>>({
    type: 'text',
    required: false
  });
  const [newStep, setNewStep] = useState<Partial<FormStep>>({
    title: '',
    description: ''
  });
  const [selectedStep, setSelectedStep] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [newOption, setNewOption] = useState<string>('');
  
  // Estados para edición de campos
  const [editingField, setEditingField] = useState<string | null>(null);
  const [editFieldData, setEditFieldData] = useState<Partial<FormField>>({});
  const [editFieldOptions, setEditFieldOptions] = useState<string[]>([]);
  const [newEditOption, setNewEditOption] = useState<string>('');
  
  const db = getFirestore();

  const formLink = userData?.businessId 
    ? `${window.location.origin}/quotation-form/${userData.businessId}`
    : '';

  useEffect(() => {
    const loadFormFields = async () => {
      if (!userData?.businessId) return;

      try {
        const formDoc = await getDoc(doc(db, 'QuotationForms', userData.businessId));
        if (formDoc.exists()) {
          const data = formDoc.data();
          // Asegurarnos de que los campos obligatorios estén presentes
          const defaultFields: FormField[] = [
            {
              id: 'clientName',
              type: 'text',
              label: t('quotations.formBuilder.defaultFields.clientName'),
              required: true,
              placeholder: t('quotations.formBuilder.defaultFields.clientNamePlaceholder')
            },
            {
              id: 'clientEmail',
              type: 'text',
              label: t('quotations.formBuilder.defaultFields.clientEmail'),
              required: true,
              placeholder: t('quotations.formBuilder.defaultFields.clientEmailPlaceholder')
            }
          ];

          // Si no hay pasos, crear uno por defecto con los campos obligatorios
          if (!data.steps || data.steps.length === 0) {
            const defaultStep: FormStep = {
              id: 'default',
              title: t('quotations.formBuilder.basicClientInfo'),
              description: t('quotations.formBuilder.defaultStepDescription'),
              fields: defaultFields
            };
            setSteps([defaultStep]);
            setSelectedStep('default');
          } else {
            // Asegurarnos de que los campos obligatorios estén en el primer paso
            const updatedSteps = [...data.steps];
            const firstStep = updatedSteps[0];
            const existingFields = firstStep.fields || [];
            
            // Filtrar campos obligatorios que ya existen
            const existingDefaultFields = existingFields.filter((field: FormField) => 
              field.id === 'clientName' || field.id === 'clientEmail'
            );
            
            // Agregar campos obligatorios faltantes
            const missingDefaultFields = defaultFields.filter((defaultField: FormField) => 
              !existingDefaultFields.some((existing: FormField) => existing.id === defaultField.id)
            );
            
            firstStep.fields = [...existingFields, ...missingDefaultFields];
            // Forzar traducción del título y descripción del primer step
            if (firstStep.id === 'default') {
              firstStep.title = t('quotations.formBuilder.basicClientInfo');
              firstStep.description = t('quotations.formBuilder.defaultStepDescription');
            }
            setSteps(updatedSteps);
            setSelectedStep(firstStep.id);
          }
        } else {
          // Si no existe el documento, crear uno con los campos por defecto
          const defaultStep: FormStep = {
            id: 'default',
            title: t('quotations.formBuilder.basicClientInfo'),
            description: t('quotations.formBuilder.defaultStepDescription'),
            fields: [
              {
                id: 'clientName',
                type: 'text',
                label: t('quotations.formBuilder.defaultFields.clientName'),
                required: true,
                placeholder: t('quotations.formBuilder.defaultFields.clientNamePlaceholder')
              },
              {
                id: 'clientEmail',
                type: 'text',
                label: t('quotations.formBuilder.defaultFields.clientEmail'),
                required: true,
                placeholder: t('quotations.formBuilder.defaultFields.clientEmailPlaceholder')
              }
            ]
          };
          setSteps([defaultStep]);
          setSelectedStep('default');
        }
      } catch (error) {
        console.error('Error loading form fields:', error);
      }
    };

    if (isOpen) {
      loadFormFields();
    }
  }, [isOpen, userData, db, t, i18n.language]);

  const handleAddStep = () => {
    if (!newStep.title) return;

    const step: FormStep = {
      id: Date.now().toString(),
      title: newStep.title,
      description: newStep.description || '',
      fields: []
    };

    setSteps(prev => [...prev, step]);
    setNewStep({ title: '', description: '' });
    setSelectedStep(step.id);
  };

  const handleRemoveStep = (id: string) => {
    // No permitir eliminar el paso por defecto si es el único
    if (steps.length === 1 && steps[0].id === 'default') {
      return;
    }

    setSteps(prev => {
      const remainingSteps = prev.filter(step => step.id !== id);
      if (remainingSteps.length > 0) {
        setSelectedStep(remainingSteps[0].id);
      } else {
        setSelectedStep('');
      }
      return remainingSteps;
    });
  };

  const handleMoveStep = (id: string, direction: 'up' | 'down') => {
    const index = steps.findIndex(step => step.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === steps.length - 1)
    ) return;

    const newSteps = [...steps];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setSteps(newSteps);
  };

  const handleAddField = () => {
    if (!newField.label || !selectedStep) return;

    const field: FormField = {
      id: Date.now().toString(),
      type: newField.type || 'text',
      label: newField.label,
      required: newField.required || false,
      options: newField.options || [],
      placeholder: newField.placeholder || ''
    };

    setSteps(prev => prev.map(step => {
      if (step.id === selectedStep) {
        return {
          ...step,
          fields: [...step.fields, field]
        };
      }
      return step;
    }));

    setNewField({
      type: 'text',
      required: false
    });
  };

  const handleRemoveField = (stepId: string, fieldId: string) => {
    // No permitir eliminar campos obligatorios
    if (fieldId === 'clientName' || fieldId === 'clientEmail') {
      return;
    }

    setSteps(prev => prev.map(step => {
      if (step.id === stepId) {
        return {
          ...step,
          fields: step.fields.filter(field => field.id !== fieldId)
        };
      }
      return step;
    }));
  };

  const handleMoveField = (stepId: string, fieldId: string, direction: 'up' | 'down') => {
    setSteps(prev => prev.map(step => {
      if (step.id === stepId) {
        const fields = [...step.fields];
        const index = fields.findIndex(field => field.id === fieldId);
        if (index === -1) return step;

        const newIndex = direction === 'up' ? index - 1 : index + 1;
        if (newIndex < 0 || newIndex >= fields.length) return step;

        [fields[index], fields[newIndex]] = [fields[newIndex], fields[index]];
        return { ...step, fields };
      }
      return step;
    }));
  };

  // Funciones para edición de campos
  const handleEditField = (fieldId: string) => {
    const field = steps
      .flatMap(step => step.fields)
      .find(f => f.id === fieldId);
    
    if (field) {
      setEditingField(fieldId);
      setEditFieldData({ ...field });
      setEditFieldOptions(field.options || []);
    }
  };

  const handleSaveFieldEdit = () => {
    if (!editingField || !editFieldData.label) return;
    
    // Validar que campos select/multiselect tengan opciones
    if ((editFieldData.type === 'select' || editFieldData.type === 'multiselect') && 
        (!editFieldOptions || editFieldOptions.length === 0)) {
      alert('Los campos select y multiselect deben tener al menos una opción');
      return;
    }
    
    // Obtener el campo original para comparar
    const originalField = steps
      .flatMap(step => step.fields)
      .find(f => f.id === editingField);
    
    // Si se cambió el tipo de campo, mostrar confirmación
    if (originalField && originalField.type !== editFieldData.type) {
      const confirmed = window.confirm(
        `¿Estás seguro de que quieres cambiar el tipo de campo de "${originalField.type}" a "${editFieldData.type}"?\n\n` +
        `Esto puede afectar los datos existentes en las cotizaciones que ya usan este campo.`
      );
      
      if (!confirmed) {
        return;
      }
    }
    
    setSteps(prev => prev.map(step => ({
      ...step,
      fields: step.fields.map(field => 
        field.id === editingField 
          ? { 
              ...field, 
              ...editFieldData, 
              options: editFieldOptions 
            }
          : field
      )
    })));
    
    setEditingField(null);
    setEditFieldData({});
    setEditFieldOptions([]);
  };

  const handleCancelFieldEdit = () => {
    setEditingField(null);
    setEditFieldData({});
    setEditFieldOptions([]);
  };

  const handleAddEditOption = () => {
    if (!newEditOption.trim()) return;
    setEditFieldOptions(prev => [...prev, newEditOption.trim()]);
    setNewEditOption('');
  };

  const handleRemoveEditOption = (index: number) => {
    setEditFieldOptions(prev => prev.filter((_, i) => i !== index));
  };

  const handleMoveEditOption = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || 
        (direction === 'down' && index === editFieldOptions.length - 1)) return;
    
    const newOptions = [...editFieldOptions];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    [newOptions[index], newOptions[newIndex]] = [newOptions[newIndex], newOptions[index]];
    setEditFieldOptions(newOptions);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(formLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Error copying link:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userData?.businessId) return;

    try {
      const formData = {
        businessId: userData.businessId,
        steps: steps,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      const formRef = doc(db, 'QuotationForms', userData.businessId);
      await setDoc(formRef, formData, { merge: true });
      
      onClose();
    } catch (error) {
      console.error('Error saving form fields:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 w-full max-w-4xl max-h-[90vh] overflow-y-auto relative">
        <button 
          onClick={() => {
            // Limpiar estados de edición al cerrar
            setEditingField(null);
            setEditFieldData({});
            setEditFieldOptions([]);
            onClose();
          }}
          className="absolute top-4 right-4 bg-transparent border-none text-gray-500 cursor-pointer p-2 flex items-center justify-center transition-colors duration-200 hover:text-red-600"
        >
          <FaTimes />
        </button>
        
        <h2 className="text-2xl font-bold text-gray-900 mb-6">
          {editingField ? (
            <span className="flex items-center gap-2">
              {t('quotations.formBuilder.title')}
              <span className="bg-blue-100 text-blue-800 text-sm px-3 py-1 rounded-full font-medium">
                Editando campo
              </span>
            </span>
          ) : (
            t('quotations.formBuilder.title')
          )}
        </h2>
        
        <form onSubmit={handleSubmit} className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <label className="text-gray-900 font-medium">{t('quotations.formBuilder.steps')}</label>
            {steps.map((step, index) => (
              <div key={step.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex-1 flex flex-col gap-1">
                  <strong className="text-gray-900">{step.title}</strong>
                  {step.description && <p className="text-gray-600 text-sm">{step.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => handleMoveStep(step.id, 'up')}
                    disabled={index === 0}
                    title={t('quotations.formBuilder.moveUp')}
                    className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <FaArrowUp />
                  </button>
                  <button
                    type="button"
                    onClick={() => handleMoveStep(step.id, 'down')}
                    disabled={index === steps.length - 1}
                    title={t('quotations.formBuilder.moveDown')}
                    className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <FaArrowDown />
                  </button>
                  {step.id !== 'default' && (
                    <button
                      type="button"
                      onClick={() => handleRemoveStep(step.id)}
                      title={t('quotations.formBuilder.remove')}
                      className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 flex items-center justify-center"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="flex flex-col gap-2">
            <label className="text-gray-900 font-medium">{t('quotations.formBuilder.addStep')}</label>
            <input
              type="text"
              value={newStep.title || ''}
              onChange={e => setNewStep(prev => ({ ...prev, title: e.target.value }))}
              placeholder={t('quotations.formBuilder.stepTitle')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
            />
            <input
              type="text"
              value={newStep.description || ''}
              onChange={e => setNewStep(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('quotations.formBuilder.stepDescription')}
              className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
            />
            <button 
              type="button" 
              onClick={handleAddStep}
              className="bg-green-600 hover:bg-green-700 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-all duration-200 flex items-center gap-2 w-fit"
            >
              <FaPlus /> {t('quotations.formBuilder.addStep')}
            </button>
          </div>

          {selectedStep && (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-medium">{t('quotations.formBuilder.fields')}</label>
                <select
                  value={selectedStep}
                  onChange={e => setSelectedStep(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                >
                  {steps.map(step => (
                    <option key={step.id} value={step.id}>
                      {step.title}
                    </option>
                  ))}
                </select>
              </div>

              {steps.find(step => step.id === selectedStep)?.fields.map((field, index) => (
                <div key={field.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {editingField === field.id ? (
                    // Modo de edición
                    <div className="flex-1 flex flex-col gap-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 font-medium">Etiqueta del campo</label>
                        <input
                          type="text"
                          value={editFieldData.label || ''}
                          onChange={e => setEditFieldData(prev => ({ ...prev, label: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                          placeholder="Etiqueta del campo"
                        />
                      </div>
                      
                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 font-medium">Tipo de campo</label>
                        <select
                          value={editFieldData.type || 'text'}
                          onChange={e => setEditFieldData(prev => ({ ...prev, type: e.target.value as FormField['type'] }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                        >
                          <option value="text">{t('quotations.formBuilder.fieldTypes.text')}</option>
                          <option value="number">{t('quotations.formBuilder.fieldTypes.number')}</option>
                          <option value="select">{t('quotations.formBuilder.fieldTypes.select')}</option>
                          <option value="multiselect">{t('quotations.formBuilder.fieldTypes.multiselect')}</option>
                          <option value="textarea">{t('quotations.formBuilder.fieldTypes.textarea')}</option>
                          <option value="date">{t('quotations.formBuilder.fieldTypes.date')}</option>
                        </select>
                      </div>

                      <div className="flex flex-col gap-2">
                        <label className="text-gray-900 font-medium">Placeholder (opcional)</label>
                        <input
                          type="text"
                          value={editFieldData.placeholder || ''}
                          onChange={e => setEditFieldData(prev => ({ ...prev, placeholder: e.target.value }))}
                          className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                          placeholder="Texto de ayuda"
                        />
                      </div>

                      {/* Edición de opciones para select/multiselect */}
                      {(editFieldData.type === 'select' || editFieldData.type === 'multiselect') && (
                        <div className="flex flex-col gap-2">
                          <label className="text-gray-700 font-medium">Opciones</label>
                          {editFieldOptions.map((opt, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-1">
                              <input
                                type="text"
                                value={opt}
                                onChange={e => {
                                  const updated = [...editFieldOptions];
                                  updated[idx] = e.target.value;
                                  setEditFieldOptions(updated);
                                }}
                                className="px-2 py-1 border border-gray-300 rounded w-48"
                              />
                              <button 
                                type="button" 
                                onClick={() => handleRemoveEditOption(idx)} 
                                className="text-red-600 hover:text-red-800 p-1"
                              >
                                <FaTrash />
                              </button>
                              <button 
                                type="button" 
                                disabled={idx === 0} 
                                onClick={() => handleMoveEditOption(idx, 'up')} 
                                className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-50"
                              >
                                <FaArrowUp />
                              </button>
                              <button 
                                type="button" 
                                disabled={idx === editFieldOptions.length - 1} 
                                onClick={() => handleMoveEditOption(idx, 'down')} 
                                className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-50"
                              >
                                <FaArrowDown />
                              </button>
                            </div>
                          ))}
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="text"
                              value={newEditOption}
                              onChange={e => setNewEditOption(e.target.value)}
                              placeholder="Agregar nueva opción"
                              className="px-2 py-1 border border-gray-300 rounded w-48"
                            />
                            <button 
                              type="button" 
                              onClick={handleAddEditOption} 
                              className="text-green-600 hover:text-green-800 p-1"
                            >
                              <FaPlus />
                            </button>
                          </div>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={editFieldData.required || false}
                          onChange={e => setEditFieldData(prev => ({ ...prev, required: e.target.checked }))}
                          className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                        />
                        <label className="text-gray-700">Campo requerido</label>
                      </div>

                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleSaveFieldEdit}
                          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                        >
                          <FaSave /> Guardar
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelFieldEdit}
                          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-medium transition-colors duration-200 flex items-center gap-2"
                        >
                          <FaUndo /> Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    // Modo de visualización normal
                    <>
                      <div className="flex-1 flex flex-col gap-1">
                        <div className="flex items-center gap-2">
                          <strong className="text-gray-900">{field.label}</strong>
                          {field.required && (
                            <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-medium">
                              Requerido
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-600">
                          <span className="bg-gray-100 px-2 py-1 rounded">Tipo: {field.type}</span>
                          {field.placeholder && (
                            <span className="text-gray-500">Placeholder: "{field.placeholder}"</span>
                          )}
                        </div>
                        {field.options && field.options.length > 0 && (
                          <div className="mt-1">
                            <span className="text-xs text-gray-500 font-medium">Opciones:</span>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {field.options.map((opt, idx) => (
                                <span 
                                  key={idx} 
                                  className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                                >
                                  {opt}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2">
                        {/* Botón de editar para todos los campos excepto los obligatorios del primer paso */}
                        {!(selectedStep === 'default' && (field.id === 'clientName' || field.id === 'clientEmail')) && (
                          <button
                            type="button"
                            onClick={() => handleEditField(field.id)}
                            title="Editar campo"
                            className="bg-blue-600 hover:bg-blue-700 text-white p-2 cursor-pointer rounded transition-all duration-200 flex items-center justify-center"
                          >
                            <FaEdit />
                          </button>
                        )}
                        {/* Ocultar mover y eliminar para los campos fijos del primer step */}
                        {!(selectedStep === 'default' && (field.id === 'clientName' || field.id === 'clientEmail')) && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleMoveField(selectedStep, field.id, 'up')}
                              disabled={index === 0}
                              title={t('quotations.formBuilder.moveUp')}
                              className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                              <FaArrowUp />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleMoveField(selectedStep, field.id, 'down')}
                              disabled={index === steps.find(s => s.id === selectedStep)?.fields.length! - 1}
                              title={t('quotations.formBuilder.moveDown')}
                              className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                            >
                              <FaArrowDown />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveField(selectedStep, field.id)}
                              title={t('quotations.formBuilder.remove')}
                              className="bg-transparent border-none text-gray-500 p-2 cursor-pointer rounded transition-all duration-200 hover:bg-gray-200 hover:text-red-600 flex items-center justify-center"
                            >
                              <FaTrash />
                            </button>
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))}

              <div className="flex flex-col gap-2">
                <label className="text-gray-900 font-medium">{t('quotations.formBuilder.addField')}</label>
                <input
                  type="text"
                  value={newField.label || ''}
                  onChange={e => setNewField(prev => ({ ...prev, label: e.target.value }))}
                  placeholder={t('quotations.formBuilder.fieldLabel')}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600"
                />
                <select
                  value={newField.type || 'text'}
                  onChange={e => setNewField(prev => ({ ...prev, type: e.target.value as FormField['type'] }))}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-red-600 bg-white"
                >
                  <option value="text">{t('quotations.formBuilder.fieldTypes.text')}</option>
                  <option value="number">{t('quotations.formBuilder.fieldTypes.number')}</option>
                  <option value="select">{t('quotations.formBuilder.fieldTypes.select')}</option>
                  <option value="multiselect">{t('quotations.formBuilder.fieldTypes.multiselect')}</option>
                  <option value="textarea">{t('quotations.formBuilder.fieldTypes.textarea')}</option>
                  <option value="date">{t('quotations.formBuilder.fieldTypes.date')}</option>
                </select>
                {/* Edición de opciones para el campo nuevo */}
                {(newField.type === 'select' || newField.type === 'multiselect') && (
                  <div className="flex flex-col gap-2 mt-2">
                    <label className="text-gray-700 font-medium">{t('quotations.formBuilder.options')}</label>
                    {(newField.options || []).map((opt, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-1">
                        <input
                          type="text"
                          value={opt}
                          onChange={e => {
                            const updated = [...(newField.options || [])];
                            updated[idx] = e.target.value;
                            setNewField(prev => ({ ...prev, options: updated }));
                          }}
                          className="px-2 py-1 border border-gray-300 rounded w-48"
                        />
                        <button type="button" onClick={() => {
                          const updated = [...(newField.options || [])];
                          updated.splice(idx, 1);
                          setNewField(prev => ({ ...prev, options: updated }));
                        }} className="text-red-600 hover:text-red-800 p-1"><FaTrash /></button>
                        <button type="button" disabled={idx === 0} onClick={() => {
                          if (idx === 0) return;
                          const updated = [...(newField.options || [])];
                          [updated[idx-1], updated[idx]] = [updated[idx], updated[idx-1]];
                          setNewField(prev => ({ ...prev, options: updated }));
                        }} className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-50"><FaArrowUp /></button>
                        <button type="button" disabled={idx === (newField.options?.length || 0) - 1} onClick={() => {
                          if (idx === (newField.options?.length || 0) - 1) return;
                          const updated = [...(newField.options || [])];
                          [updated[idx+1], updated[idx]] = [updated[idx], updated[idx+1]];
                          setNewField(prev => ({ ...prev, options: updated }));
                        }} className="text-gray-500 hover:text-gray-800 p-1 disabled:opacity-50"><FaArrowDown /></button>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="text"
                        value={newOption}
                        onChange={e => setNewOption(e.target.value)}
                        placeholder={t('quotations.formBuilder.addOption')}
                        className="px-2 py-1 border border-gray-300 rounded w-48"
                      />
                      <button type="button" onClick={() => {
                        if (!newOption) return;
                        setNewField(prev => ({
                          ...prev,
                          options: [...(prev.options || []), newOption]
                        }));
                        setNewOption('');
                      }} className="text-green-600 hover:text-green-800 p-1"><FaPlus /></button>
                    </div>
                  </div>
                )}
                <label className="flex items-center gap-2 text-gray-700">
                  <input
                    type="checkbox"
                    checked={newField.required || false}
                    onChange={e => setNewField(prev => ({ ...prev, required: e.target.checked }))}
                    className="w-4 h-4 text-red-600 border-gray-300 rounded focus:ring-red-500"
                  />
                  {t('quotations.formBuilder.required')}
                </label>
                <button 
                  type="button" 
                  onClick={handleAddField}
                  className="bg-green-600 hover:bg-green-700 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-all duration-200 flex items-center gap-2 w-fit"
                >
                  <FaPlus /> {t('quotations.formBuilder.addField')}
                </button>
              </div>
            </>
          )}

          <button 
            type="submit"
            className="bg-red-600 hover:bg-red-700 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-colors duration-200"
          >
            {t('quotations.formBuilder.save')}
          </button>

          <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('quotations.formBuilder.formLink')}</h3>
            <p className="text-gray-600 mb-4">{t('quotations.formBuilder.formLinkDescription')}</p>
            <div className="flex items-center gap-4">
              <input
                type="text"
                value={formLink}
                readOnly
                onClick={(e) => e.currentTarget.select()}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-base bg-white cursor-text select-all"
              />
              <button 
                type="button" 
                onClick={handleCopyLink}
                className="bg-red-600 hover:bg-red-700 text-white border-none px-6 py-3 rounded-lg cursor-pointer text-base font-medium transition-colors duration-200"
              >
                {copySuccess ? t('quotations.formBuilder.copied') : t('quotations.formBuilder.copy')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuotationFormBuilder; 